import { SCAN_CG } from '../../config.js';

/**
 * 家具のかたまりの大きさから、何の家具かを当てる。
 *
 * スキャンから分かるのは「そこに物がある」ことだけなので、床からの高さと平面の大きさ、
 * それに縦横の比で見分ける。基準は日本の住まいの一般的な寸法。
 *
 * 当てられないものは null を返し、スキャンの色を付けた箱のまま置く。
 * 無理に当てると、実際には無い家具を見せることになるので、迷ったら箱のままにする。
 *
 * トイレ・洗面台・洗濯機は大きさが似ていて取り違えやすいので、
 * 近くの視点の名前（「洗面所」「浴室」など）で水回りかどうかを確かめてから当てる。
 */

/** 水回りの視点につく言葉。この近くだけで水回りの設備を当てる */
const WET_WORDS = ['トイレ', '洗面', '浴室', '風呂', 'バス', 'サニタリー', '洗濯', 'ユニット'];

/**
 * 見分ける家具。上から順に調べ、最初に当てはまったものを使う。
 * h/long/short は m、ratio は長辺÷短辺。
 * zone は置かれる場所（wet=水回り、room=居室、any=どこでも）。
 * size は大きさを実測に合わせる方法。turn は「既定で長辺が x 方向か」。
 */
const KINDS = [
  { type: 'bathtub', zone: 'wet', h: [0.32, 0.8], long: [1.0, 1.9], short: [0.55, 1.1], ratio: [1.3, 3.0], size: 'wd' },
  { type: 'toilet', zone: 'wet', h: [0.55, 1.05], long: [0.5, 0.95], short: [0.28, 0.55], ratio: [1.3, 2.6], turn: false },
  { type: 'washer', zone: 'wet', h: [0.75, 1.2], long: [0.45, 0.8], short: [0.42, 0.78], ratio: [1.0, 1.3] },
  { type: 'basin', zone: 'wet', h: [0.62, 1.05], long: [0.5, 1.3], short: [0.3, 0.65], ratio: [1.2, 3.0], size: 'wd' },
  { type: 'bed', zone: 'room', h: [0.18, 0.75], long: [1.7, 2.3], short: [0.8, 1.8], ratio: [1.1, 2.6], size: 'wd' },
  { type: 'sofa', zone: 'room', h: [0.5, 1.05], long: [1.2, 2.5], short: [0.6, 1.2], ratio: [1.3, 3.2], turn: true },
  // 小さな台はトイレや洗面台と大きさが被るので、机と言い切れる大きさだけ当てる
  { type: 'table', zone: 'any', h: [0.5, 0.92], long: [0.8, 2.3], short: [0.5, 1.3], ratio: [1.0, 3.0], size: 'wd' },
  { type: 'chair', zone: 'any', h: [0.68, 1.15], long: [0.33, 0.66], short: [0.3, 0.66], ratio: [1.0, 1.5] },
];

/**
 * @param cluster [{ gx, gz, top }] 家具のかたまり
 * @param cell    マスの大きさ(m)
 * @param minX    グリッドの原点
 * @param minZ    グリッドの原点
 * @param floorY  その家具が乗っている床の高さ(m)
 * @param top     天面の高さ(m)。省略するとマスの top の最大値を使う
 * @param spots   [{ name, x, z }] 視点。近くの部屋の名前を知るのに使う
 * @returns buildHouse の家具1件、または null（当てられなかった）
 */
export function guessFurniture(cluster, { cell, minX, minZ, floorY, top = null, spots = [] }) {
  let x0 = Infinity;
  let x1 = -Infinity;
  let z0 = Infinity;
  let z1 = -Infinity;
  let highest = -Infinity;
  for (const c of cluster) {
    x0 = Math.min(x0, c.gx);
    x1 = Math.max(x1, c.gx);
    z0 = Math.min(z0, c.gz);
    z1 = Math.max(z1, c.gz);
    highest = Math.max(highest, c.top ?? -Infinity);
  }
  const w = (x1 - x0 + 1) * cell;
  const d = (z1 - z0 + 1) * cell;
  const h = (top ?? highest) - floorY;
  const long = Math.max(w, d);
  const short = Math.min(w, d);
  // マスがまばらなら、家具の形をなしていない（スキャンの拾いこぼし）
  if ((cluster.length * cell * cell) / (w * d) < SCAN_CG.guessMinFill) return null;

  const x = x0 * cell + minX + w / 2;
  const z = z0 * cell + minZ + d / 2;
  const zone = zoneAt(x, z, spots);
  const kind = KINDS.find(
    (k) =>
      (k.zone === 'any' || k.zone === zone) &&
      inRange(h, k.h) &&
      inRange(long, k.long) &&
      inRange(short, k.short) &&
      inRange(long / short, k.ratio),
  );
  if (kind === undefined) return null;

  const item = { type: kind.type, x, y: floorY, z };
  if (kind.size === 'wd') {
    item.w = w;
    item.d = d;
  }
  // 大きさを変えられない家具は、長辺の向きに合わせて回す
  if (kind.turn !== undefined) item.rot = (w >= d) === kind.turn ? 0 : 90;
  return item;
}

/** その場所が水回りか居室か。近くに視点が無ければ居室とみなす */
function zoneAt(x, z, spots) {
  let best = null;
  let nearest = SCAN_CG.guessRoomRadius;
  for (const spot of spots) {
    const dist = Math.hypot(spot.x - x, spot.z - z);
    if (dist > nearest) continue;
    nearest = dist;
    best = spot.name ?? '';
  }
  return best !== null && WET_WORDS.some((word) => best.includes(word)) ? 'wet' : 'room';
}

const inRange = (v, [lo, hi]) => v >= lo && v <= hi;
